import { gql, useQuery } from '@apollo/client';
import { ThemeProvider as OrbitThemeProvider } from '@apollo/orbit';
import {
  SpaceKitProvider,
  useSpaceKitProvider,
} from '@apollo/space-kit/SpaceKitProvider';
import * as Sentry from '@sentry/react';
import useIsChromatic from 'chromatic';
import classnames from 'classnames';
import type { CSSProperties } from 'react';
import React, { useEffect } from 'react';

import { useLocalStorage } from 'src/hooks/useLocalStorage';
import type { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useIsLoggedIn } from '../../app/graph/hooks/useIsLoggedIn';

export enum ThemeName {
  DARK = 'dark',
  LIGHT = 'light',
}

const prefersDarkMediaQuery =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

const subscribeToPrefersDark = (callback: () => void): (() => void) => {
  if (!prefersDarkMediaQuery) return () => {};
  prefersDarkMediaQuery.addEventListener('change', callback);
  return () => {
    prefersDarkMediaQuery.removeEventListener('change', callback);
  };
};

const getPrefersDarkSnapshot = (): boolean =>
  prefersDarkMediaQuery?.matches ?? false;

type ThemeState = ReturnType<typeof useThemeState>;

const ThemeContext = React.createContext<ThemeState | null>(null);

const themedClasses = ['theme-container-light', 'theme-container-dark'];

export const ThemeProvider = ({
  children,
  className,
  styles,
  overrideThemeName,
  setGlobalTheme = false,
  __isInternalWIP = false,
}: {
  children: React.ReactNode;
  styles?: CSSProperties;
  overrideThemeName?: ThemeName;
  className?: string;
  /**
   * TODO(rebrand-rollout)
   * This is a boolean that we pass into our app-level theme wrapper in
   * main.tsx.
   *
   * At some point, if we want to roll theming out across the whole app,
   * we would remove the nested <ThemeProvider> around the Explorer
   * and just have the whole app wrapped in this one provider instead.
   *
   * For now, we use localStorage to remember this preference for internal
   * users who are contributing to the WIP.
   *
   */
  __isInternalWIP?: boolean;
  /**
   * TODO(dark-mode)
   * We need this to set some CSS straight on `body`, at the top most level
   * off the app. It's only used in main.tsx. We will be able to remove this bool
   * once there is only one ThemeProvider for the app and no more nesting. This
   * should be possible when we're ready to roll out dark  mode.
   */
  setGlobalTheme?: boolean;
}) => {
  const themeState = useThemeState(overrideThemeName, __isInternalWIP);
  const themeContainerRef = React.createRef<HTMLDivElement>();

  useEffect(() => {
    if (
      themeState.themeName === 'light' &&
      themeContainerRef.current?.closest('.theme-container-dark')
    ) {
      Sentry.captureException(
        new Error(
          "light mode used in dark mode. tailwind can't handle this nested class case",
        ),
      );
    }
  }, [themeContainerRef, themeState.themeName]);

  useEffect(() => {
    if (setGlobalTheme) {
      // we want to do this globally on the <body> tag to encompass portals
      document.body.classList.remove(...themedClasses);
      document.body.classList.add(
        ...[
          themeState.themeName === 'light'
            ? 'theme-container-light'
            : 'theme-container-dark',
          // this needs to be here and not just on <body> in index.html
          // so that our storybook stories are treated the same as our app itself
          'text-body',
          'bg-secondary-inverted',
        ],
      );
    }
  }, [setGlobalTheme, themeState]);

  const spaceKitProvider = useSpaceKitProvider();
  /**
   * TODO(rebrand-rollout)
   * When we roll out Orbit globally after our FF has been stable for a little bit,
   * can we remove the SpaceKitProvider wrapper here entirely? What does it still do?
   * Can we merge OrbitThemeProvider with ThemeProvider and just have one?
   */
  return (
    <ThemeContext value={themeState}>
      <SpaceKitProvider
        {...spaceKitProvider}
        disableAnimations={
          useIsChromatic() || spaceKitProvider.disableAnimations
        }
        theme={themeState.themeName}
      >
        <OrbitThemeProvider>
          <div
            className={classnames(
              className,
              !setGlobalTheme &&
                (themeState.themeName === 'light'
                  ? 'theme-container-light'
                  : 'theme-container-dark'),
            )}
            // we add colorScheme here so we can get native dark-mode scroll bars
            // from the user's browser when appropriate
            style={{ ...styles, colorScheme: themeState.themeName }}
            ref={themeContainerRef}
          >
            {children}
          </div>
        </OrbitThemeProvider>
      </SpaceKitProvider>
    </ThemeContext>
  );
};

export const useThemeContext = () => {
  const context = React.use(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

const useThemeState = (
  overrideThemeName?: ThemeName,
  __isInternalWIP?: boolean,
) => {
  const { isLoggedIn } = useIsLoggedIn();
  // null means "use system theme"
  const [temporaryInternalGlobalThemeName] = useLocalStorage(
    'temporaryInternalGlobalThemeName',
  );
  const { data } = useQuery(
    gql<
      GraphQLTypes.UserThemeQuery,
      GraphQLTypes.UserThemeQueryVariables
    >/* GraphQL */ `
      query UserThemeQuery {
        studioSettings {
          id
          themeName
        }
      }
    `,
    { skip: Boolean(overrideThemeName) || __isInternalWIP || !isLoggedIn },
  );

  const [{ themeName: localThemeName }] = useLocalStorage(
    'localExplorerSettings',
  );
  const [hasTouchedDarkModeToggle] = useLocalStorage(
    'hasTouchedDarkModeToggle',
  );
  const systemPrefersDark = React.useSyncExternalStore(
    subscribeToPrefersDark,
    getPrefersDarkSnapshot,
  );
  const systemTheme = systemPrefersDark ? 'DARK' : 'LIGHT';

  // Track the current system theme reactively for __isInternalWIP + system mode
  const [currentSystemTheme, setCurrentSystemTheme] = React.useState<ThemeName>(
    () =>
      window.matchMedia('(prefers-color-scheme: dark)').matches
        ? ThemeName.DARK
        : ThemeName.LIGHT,
  );

  // When in global theme mode and system is selected (null), listen for OS theme changes
  useEffect(() => {
    if (!__isInternalWIP || temporaryInternalGlobalThemeName !== null) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    // Sync immediately to current system theme
    setCurrentSystemTheme(
      mediaQuery.matches ? ThemeName.DARK : ThemeName.LIGHT,
    );
    const handler = (e: MediaQueryListEvent) => {
      setCurrentSystemTheme(e.matches ? ThemeName.DARK : ThemeName.LIGHT);
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [__isInternalWIP, temporaryInternalGlobalThemeName]);

  const pickedTheme = isLoggedIn
    ? data?.studioSettings?.themeName
    : localThemeName;
  const explorerSettingThemeName =
    !hasTouchedDarkModeToggle &&
    // This check is to ensure users who have switched from default to dark mode
    // don't have their preferences reset.
    pickedTheme !== 'DARK'
      ? systemTheme
      : pickedTheme;

  const themeName =
    overrideThemeName ??
    (__isInternalWIP
      ? // null means system mode; resolve to current system theme reactively
        (temporaryInternalGlobalThemeName ?? currentSystemTheme)
      : explorerSettingThemeName
        ? ThemeName[explorerSettingThemeName]
        : ThemeName.LIGHT);

  /* when using for classNames, always wrap with tw`` or use `classnames` function  */
  const themedValue = React.useCallback(
    // eslint will format this without the generic value so we need to disable the rule
    // eslint-disable-next-line prefer-arrow-callback
    function fn<T>(dict: Record<ThemeName, T>) {
      return dict[themeName];
    },
    [themeName],
  );

  return React.useMemo(
    () => ({
      themeName,
      /**
       * @deprecated use semantic Tailwind tokens instead
       */
      themedValue,
    }),
    [themeName, themedValue],
  );
};
